/**
 * F5 — auto-derived "distance from client" ordering for derivedContexts. This is
 * the out-of-the-box DEFAULT row order in the matrix views: client-facing
 * derivedContexts surface first, backend / data / infrastructure ("pipes") sink
 * toward the back.
 *
 * It's deterministic and only DIRECTIONALLY correct — a keyword heuristic over
 * the derivedContext name + its skill names — meant as a sensible starting point
 * the user can hand-tweak later. A future /setup manual re-order will persist
 * an explicit order that overrides this default everywhere.
 */

/** Signals a derivedContext sits close to the client / product surface. */
const FRONT = [
  'frontend', 'front-end', 'ui', 'ux', 'user interface', 'client', 'customer',
  'portal', 'dashboard', 'mobile', 'web', 'react', 'vue', 'angular', 'svelte',
  'css', 'tailwind', 'design', 'presentation', 'component', 'marketing',
  'sales', 'onboarding', 'notification', 'messaging', 'chat', 'email',
];

/** Signals a derivedContext sits deep in the stack (backend / data / infra). */
const BACK = [
  'backend', 'back-end', 'infrastructure', 'infra', 'platform', 'devops',
  'sre', 'database', 'sql', 'postgres', 'mysql', 'redis', 'mongo', 'pipeline',
  'ingestion', 'etl', 'warehouse', 'queue', 'kafka', 'stream', 'network',
  'edge', 'kubernetes', 'docker', 'terraform', 'ansible', 'observability',
  'monitoring', 'logging', 'alerting', 'security', 'storage', 'cache',
  'worker', 'scheduler', 'batch', 'cron',
];

type Rankable = { display: string; skills: ReadonlyArray<{ display: string }> };

function haystack(comp: Rankable): string {
  return `${comp.display} ${comp.skills.map((s) => s.display).join(' ')}`.toLowerCase();
}

/**
 * Rank a derivedContext by distance from the client — LOWER = closer (shown
 * first). Each front-ish keyword pulls it forward, each back-ish keyword
 * pushes it back; a neutral derivedContext lands at 0 (the middle).
 */
export function clientDistanceRank(comp: Rankable): number {
  const t = haystack(comp);
  let rank = 0;
  for (const k of FRONT) if (t.includes(k)) rank -= 1;
  for (const k of BACK) if (t.includes(k)) rank += 1;
  return rank;
}

/**
 * Stable client-distance sort: client-facing derivedContexts first, backend /
 * infra last, ties broken alphabetically by display. Pure (returns a copy).
 */
export function sortByClientDistance<C extends Rankable>(comps: C[]): C[] {
  return [...comps].sort(
    (a, b) => clientDistanceRank(a) - clientDistanceRank(b) || a.display.localeCompare(b.display),
  );
}
